import { memo } from 'react';

import { visibleCellsInRenderOrder } from '../../core/render-order';
import type { DataGridDensity, RowData, DataGridRow as TanStackRow } from '../../types';
import type { DataGridVariant } from '../data-grid';
import { rowVariants } from '../data-grid.variants';
import { DataGridCell } from './data-grid-cell';

/** Which cell in this row renders the disclosure toggle, and what it is called. */
export interface RowExpansionToggle {
  columnId: string;
  label: string;
  onToggle: () => void;
}

export interface DataGridRowProps<TRow extends RowData> {
  row: TanStackRow<TRow>;
  ariaRowIndex: number;
  /** Visible columns, their pinning, and the pinned ones' widths, encoded as one string — see the
   * custom comparator below. An unpinned column's width is not in it: the `<colgroup>` owns that,
   * so resizing one invalidates no row. */
  columnLayoutKey: string;
  /**
   * A plain snapshot of `row.getIsSelected()`, taken by `DataGridBody` — never read from `row`
   * directly in this component. `row.getIsSelected()` is a live method that re-reads current table
   * state on every call, so comparing it against itself inside `propsAreEqual` below (same `row`
   * object before and after) always reads as unchanged; a plain boolean prop is something React's
   * normal prop diffing can actually tell apart across renders.
   */
  selected: boolean;
  /**
   * Whether this grid instance has the selection system column at all — `row.getCanSelect()` alone
   * isn't enough: it reflects TanStack's own `enableRowSelection`, which defaults to `true` at the
   * table level regardless of whether this package was told to render a select column. Without this
   * flag, a grid built with no `enableRowSelection` option would still get `aria-selected="false"`
   * on every row, announcing a selection capability that doesn't exist for it.
   */
  selectable: boolean;
  /**
   * Roving tabindex (vertical row navigation via arrow keys, deliberately not a full per-cell 2D
   * grid-navigation model) — exactly one row in the body is ever a Tab stop. `DataGrid`
   * body owns which row id that is; this component only renders the resulting boolean and reports
   * back when it receives focus (by click, Tab, or arrow-key navigation from a sibling row).
   */
  active: boolean;
  onActivate: () => void;
  density?: DataGridDensity;
  variant?: DataGridVariant;
  striped?: boolean;
  /**
   * Reaches every cell, un-clamping the clamped ones. Held apart from `toggle` because a row can be
   * open while offering no control — the value it was revealing can go away under it.
   */
  expanded?: boolean;
  /** Absent, this row shows no toggle: expansion is off, or it has nothing to reveal. */
  toggle?: RowExpansionToggle;
}

function DataGridRowImpl<TRow extends RowData>({
  row,
  ariaRowIndex,
  columnLayoutKey,
  selected,
  selectable,
  active,
  onActivate,
  density,
  variant,
  striped,
  expanded,
  toggle,
}: DataGridRowProps<TRow>) {
  // Built once per row rather than per cell: it does not vary by cell, only which cell receives it
  // does. The per-cell test is then a plain string comparison.
  const toggleColumnId = toggle?.columnId;
  const cellExpansion =
    toggle === undefined
      ? undefined
      : { expanded: expanded ?? false, label: toggle.label, onToggle: toggle.onToggle };
  return (
    <tr
      aria-rowindex={ariaRowIndex}
      // Omitted entirely when this row can't be selected at all (grid-wide, via `selectable`, or
      // per-row, via TanStack's own `row.getCanSelect()` predicate) — asserting "not selected" on a
      // row that was never selectable to begin with claims a capability this grid instance doesn't
      // have.
      aria-selected={selectable && row.getCanSelect() ? selected : undefined}
      data-state={selected ? 'selected' : undefined}
      tabIndex={active ? 0 : -1}
      onFocus={onActivate}
      onKeyDown={(event) => {
        // Only when the row itself is the focused element, not a bubbled key event from an
        // interactive cell (a checkbox, the actions menu trigger) — those own their own key
        // handling (e.g. a native `<select>`'s up/down), which this must not hijack.
        if (event.target !== event.currentTarget) return;
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          const next = event.currentTarget.nextElementSibling;
          if (next instanceof HTMLElement) next.focus();
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          const previous = event.currentTarget.previousElementSibling;
          if (previous instanceof HTMLElement) previous.focus();
        }
      }}
      className={rowVariants({ striped, selected })}
    >
      {visibleCellsInRenderOrder(row).map((cell) => (
        <DataGridCell
          key={cell.id}
          cell={cell}
          columnLayoutKey={columnLayoutKey}
          selected={selected}
          {...(expanded === undefined ? {} : { rowExpanded: expanded })}
          {...(cellExpansion !== undefined && cell.column.id === toggleColumnId
            ? { expansion: cellExpansion }
            : {})}
          {...(density === undefined ? {} : { density })}
          {...(variant === undefined ? {} : { variant })}
        />
      ))}
    </tr>
  );
}

// Row id, row data, selection state, active (roving-tabindex) state, `ariaRowIndex`, and
// `columnLayoutKey` — grouping state and a virtualization data-index join this list once those
// features exist; comparing on fields that are always absent today would be dead weight, not
// correctness. `onActivate` is deliberately excluded, same as every other callback prop this
// component takes — see the perf notes on handler stability in this package's own plan history;
// a fresh closure per render is expected and never gates the memo.
function propsAreEqual<TRow extends RowData>(
  prev: DataGridRowProps<TRow>,
  next: DataGridRowProps<TRow>,
): boolean {
  return (
    prev.row.id === next.row.id &&
    prev.row.original === next.row.original &&
    prev.selected === next.selected &&
    prev.selectable === next.selectable &&
    prev.active === next.active &&
    prev.ariaRowIndex === next.ariaRowIndex &&
    prev.columnLayoutKey === next.columnLayoutKey &&
    prev.density === next.density &&
    prev.variant === next.variant &&
    prev.striped === next.striped &&
    prev.expanded === next.expanded &&
    // By value: `toggle` is rebuilt every render because its `onToggle` closes over the row id.
    prev.toggle?.columnId === next.toggle?.columnId &&
    prev.toggle?.label === next.toggle?.label
  );
}

export const DataGridRow = memo(DataGridRowImpl, propsAreEqual) as typeof DataGridRowImpl;
